<template>
	<PanelDraggable :data="data" :draggable="isDraggable">
		<div>

			<!-- HEADER -->
			<Button v-if="dashboards.editMode" size="small" severity="danger" class="dash-delete-btn" @click="remove()">
				<GlyphIcon :icon="GlyphIconName['bin']" color="#fff" size="18" />
			</Button>
			<div class="dash-header" @mouseover="mouseOverHeader(true)" @mouseout="mouseOverHeader(false)" :editMode="dashboards.editMode" :class="{'queue-yellow': commonState === CommonState.YELLOW, 'queue-red': commonState === CommonState.RED}">
				<GlyphIcon :icon="GlyphIconName['user-parents']" color="#fff" size="18" style="margin-top:2px" />
				<div class="dotdotdot" style="margin-left:6px;margin-top:1px">{{ headerTitle }}<span v-if="collapsed">&nbsp;({{ totalCalls }})</span></div>
				<div v-show="!dashboards.editMode" class="flex flex-row items-start gap-1.5 justify-between">
					<Button v-if="dynamicQueues.length > 0" size="small" variant="text" class="header-button" @click="toggleMembershipMenu" style="padding: 2px 6px;">
						<GlyphIcon :icon="GlyphIconName['user-group']" size="17" />
					</Button>
					<Menu ref="membershipMenu" :model="membershipMenuItems" :popup="true" />
					<Button size="small" variant="text" class="header-button" @click="toggleCollapse()">
						<GlyphIcon :icon="GlyphIconName['chevron-up']" size="19" class="transition-transform" :class="{ 'upside-down': collapsed }" />
					</Button>
				</div>
			</div>

			<!-- EDIT MODE -->
			<div v-if="dashboards.editMode" class="dash-settings-box">
				<div class="w-full mb-3">
					<div>{{ t('extensions.panelName') }}</div>
					<InputText v-model="settings.title" size="small" fluid :required="true" maxlength="64" />
				</div>

				<div class="w-full mb-3">
					<div>{{ t('multiQueue.queues') }}</div>
					<MultiSelect fluid v-model="settings._ids" :options="availableQueues" filter
						option-value="value" option-label="label" size="small" :placeholder="t('multiQueue.selectQueues')" append-to="self" class="multiqueue-select" />
				</div>

				<div class="w-full mb-3">
					<div>{{ t('queue.showSection') }}</div>
					<div class="flex flex-row justify-between gap-0.5">
						<div class="flex items-center gap-3">
							<ToggleSwitch v-model="settings.showCalls" size="small" />
							<div class="section-label">{{ t('queue.showCallsInQueue') }}</div>
						</div>
						<div class="flex items-center gap-3">
							<ToggleSwitch v-model="settings.showMembers" size="small" />
							<div class="section-label">{{ t('queue.showMembers') }}</div>
						</div>
						<div class="flex items-center gap-3">
							<ToggleSwitch v-model="settings.showStats" size="small" />
							<div class="section-label">{{ t('queue.showStatistics') }}</div>
						</div>
					</div>
				</div>

				<div class="w-full mb-3 flex gap-4">
					<div class="w-1/2">
						<div>{{ t('queue.numberOfCalls') }}</div>
						<InputNumber v-model="settings.maxCalls" showButtons buttonLayout="horizontal" size="small" :step="1" :min="1" :max="20" fluid :disabled="!settings.showCalls">
							<template #incrementicon>
								<span class="pi pi-plus" />
							</template>
							<template #decrementicon>
								<span class="pi pi-minus" />
							</template>
						</InputNumber>
						<div class="variable-info pt-3">{{ t('queue.numberOfCallsDescription') }}</div>
					</div>
					<div class="w-1/2">
						<div>{{ t('queue.numberOfMembers') }}</div>
						<InputNumber v-model="settings.maxMembers" showButtons buttonLayout="horizontal" size="small" :step="1" :min="1" :max="20" fluid :disabled="!settings.showMembers">
							<template #incrementicon>
								<span class="pi pi-plus" />
							</template>
							<template #decrementicon>
								<span class="pi pi-minus" />
							</template>
						</InputNumber>
						<div class="variable-info pt-3">{{ t('queue.numberOfMembersDescription') }}</div>
					</div>
				</div>

				<div class="w-full mb-3 flex gap-4" v-if="data.showCalls">
					<div class="w-1/2">
						<div>{{ t('queue.callVariableName') }}</div>
						<InputText size="small" maxlength="64" fluid v-model="settings.variable" @keypress="inputValidator($event, /[a-zA-Z0-9-_]/)" @paste="inputPasteValidator($event, /^[a-zA-Z0-9-_]+$/)" />
					</div>
					<div class="w-1/2">
						<div>{{ t('queue.callVariableLabel') }}</div>
						<InputText size="small" maxlength="32" fluid v-model="settings.variableName" />
					</div>
				</div>
				<div class="variable-info" v-if="data.showCalls">{{ t('queue.callVariableDescription') }}</div>

				<div v-if="data.showStats">{{ t('queue.statisticsElementsHeader') }}</div>
				<div v-if="data.showStats" class="w-full flex flex-wrap gap-1 mb-1 stats-edit-box">
					<div class="stats-edit-item" @click="removeStat(item)" v-for="item in settings.stats" :key="item" v-html="t('queue.stats.' + item)"></div>
					<div v-if="settings.stats.length === 0" class="w-full text-center italic text-sm text-muted-color p-1">{{ t('common.noneSelected') }}</div>
				</div>
				<Select v-if="data.showStats" :placeholder="t('queue.addStatistics')" v-model="statsPicker" :options="statsAvailable" size="small" option-value="value" option-label="label" @change="addStat" fluid :disabled="statsAvailable.length === 0" append-to="self" />
			</div>

			<!-- VIEW MODE -->
			<div v-if="!dashboards.editMode && data._ids.length > 0" class="view-box" :style="{'max-height': (collapsed? '0px' : '2000px')}">

				<CallDraggable :data="combinedQueues" :type="CallDragTargetType.QueueCallers" :target="true" :draggable="false" :targetTypes="[CallDragItemType.MyCallsCall, CallDragItemType.QueueCall]">
					<div v-if="data.showCalls">
						<div class="section-header">
							<div>
								{{ t('queue.queueSectionHeader') }}
								<span v-if="collapsedCallers || totalCalls > settings.maxCalls">&nbsp;({{ totalCalls }})</span>
							</div>
							<div>
								<Button size="small" variant="text" class="subheader-button" @click="toggleCollapseCallers()">
									<GlyphIcon :icon="GlyphIconName['chevron-up']" size="15" class="transition-transform" :class="{'upside-down': collapsedCallers}" />
								</Button>
							</div>
						</div>
						<div class="vertical-scroll" style="transition:500ms" :style="{'max-height': (collapsedCallers ? 0 : data.maxCalls*45 + 10)+'px','min-height': (collapsedCallers ? 0 : data.maxCalls*45 + 10)+'px'}">
							<transition-group name="list2" tag="div">
								<div v-for="(call, index) in sortedCalls" :key="call._id" class="list-item">
									<MultiQueueCall :call="call" :queueId="call.queueId" :queueName="call.queueName" :index="index" :variable="data.variable" :variableName="data.variableName" :last="sortedCalls.length-1==index" :serviceLevel="call.serviceLevel" :serviceLevelCritical="call.serviceLevelCritical" />
								</div>
							</transition-group>
						</div>
					</div>
				</CallDraggable>

				<CallDraggable :data="combinedQueues" :type="CallDragTargetType.QueueMembers" :target="true" :draggable="false" :targetTypes="[CallDragItemType.Employee, CallDragItemType.QueueMember]">
					<div v-if="data.showMembers">
						<div class="section-header">
							<div>
								{{ t('queue.memberSectionHeader') }}
								<span v-if="collapsedMembers || uniqueMembers.length > settings.maxMembers">&nbsp;({{ uniqueMembers.length }})</span>
							</div>
							<div class="flex items-center gap-1">
								<Button size="small" variant="text" class="header-button" @click="toggleCollapseMembers()">
									<GlyphIcon :icon="GlyphIconName['chevron-up']" class="transition-transform" :class="{ 'upside-down': collapsedMembers }" size="15" />
								</Button>
							</div>
						</div>
						<div class="vertical-scroll" style="transition:500ms" :style="{'max-height': (collapsedMembers ? 0 : data.maxMembers*52 + 20)+'px','min-height': (collapsedMembers ? 0 : data.maxMembers*52 + 20)+'px'}">
							<transition-group name="list2" tag="div">
								<div v-for="(member, index) in uniqueMembers" :key="member._id" class="list-item">
									<MultiQueueMember :member="member" :index="index" :queueIds="member.queueIds" :queueNames="member.queueNames" :last="uniqueMembers.length-1==index" />
								</div>
							</transition-group>
						</div>
					</div>
				</CallDraggable>

				<div v-if="data.showStats">
					<div class="section-header">
						<div>
							{{ t('queue.statisticsSectionHeader') }}
							<span v-if="collapsedStatistics">&nbsp;({{data.stats.length }})</span>
						</div>
						<div>
							<Button size="small" variant="text" class="header-button" @click="toggleCollapseStatistics()">
								<GlyphIcon :icon="GlyphIconName['chevron-up']" class="transition-transform" :class="{ 'upside-down': collapsedStatistics }" size="15" />
							</Button>
						</div>
					</div>
					<div class="stats-box" style="transition:500ms" :style="{'max-height': (collapsedStatistics ? '0px' : statsHeight)}">
						<div v-for="key in data.stats" :key="key">
							<div v-if="key=='NOMEMBERS'" class="stats-head" v-html="t('queue.stats.NOMEMBERS')"></div>
							<div v-if="key=='ALLBUSY'" class="stats-head" v-html="t('queue.stats.ALLBUSY')"></div>
							<div v-if="key=='TIMEOUT'" class="stats-head">{{ t('queue.stats.TIMEOUT1') }}<br>{{ ss.settings.dashboardColumnWidth < 370 ? '' : t('queue.stats.TIMEOUT2') }} {{ t('queue.stats.TIMEOUT3') }}</div>
							<div v-if="key=='CANCELLED'" class="stats-head" v-html="t('queue.stats.CANCELLED')"></div>
							<div v-if="key=='ANSWERED'" class="stats-head" v-html="t('queue.stats.ANSWERED')"></div>
							<div v-if="key=='UNANSWERED'" class="stats-head" v-html="t('queue.stats.UNANSWERED')"></div>
							<div v-if="key=='TOTAL'" class="stats-head" v-html="t('queue.stats.TOTAL')"></div>
							<div v-if="key=='AVERAGEWAITTIME'" class="stats-head" v-html="t('queue.stats.AVERAGEWAITTIME')"></div>
							<div v-if="key=='AVERAGETALKTIME'" class="stats-head" v-html="t('queue.stats.AVERAGETALKTIME')"></div>
							<div v-if="key=='TOTALTALKTIME'" class="stats-head" v-html="t('queue.stats.TOTALTALKTIME')"></div>
							<div v-if="key=='LONGESTWAITTIME'" class="stats-head" v-html="t('queue.stats.LONGESTWAITTIME')"></div>
							<div v-if="key=='LASTWAITTIME'" class="stats-head" v-html="t('queue.stats.LASTWAITTIME')"></div>
							<div v-if="key=='SERVICELEVEL'" class="stats-head" v-html="t('queue.stats.SERVICELEVEL')"></div>
							<div v-if="key=='SERVICELEVELCRITICAL'" class="stats-head">{{ (ss.settings.dashboardColumnWidth < 370 ? t('queue.stats.SERVICELEVELCRITICAL1') : t('queue.stats.SERVICELEVELCRITICAL2')) }}<br>{{ t('queue.stats.SERVICELEVELCRITICAL3') }}</div>

							<div v-if="key=='NOMEMBERS'" class="stats-value">{{ aggregatedStats.noMembers }}</div>
							<div v-if="key=='ALLBUSY'" class="stats-value">{{ aggregatedStats.allBusy }}</div>
							<div v-if="key=='TIMEOUT'" class="stats-value">{{ aggregatedStats.timeout }}</div>
							<div v-if="key=='CANCELLED'" class="stats-value">{{ aggregatedStats.cancelled }}</div>
							<div v-if="key=='ANSWERED'" class="stats-value">{{ aggregatedStats.answered }}</div>
							<div v-if="key=='UNANSWERED'" class="stats-value">{{ aggregatedStats.unanswered }}</div>
							<div v-if="key=='TOTAL'" class="stats-value">{{ aggregatedStats.total }}</div>
							<div v-if="key=='AVERAGEWAITTIME'" class="stats-value">{{ shortduration(aggregatedStats.averageWaitTime, false) }}<span class="stats-value-label">{{ shortduration(aggregatedStats.averageWaitTime, true) }}</span></div>
							<div v-if="key=='AVERAGETALKTIME'" class="stats-value">{{ shortduration(aggregatedStats.averageTalkTime, false) }}<span class="stats-value-label">{{ shortduration(aggregatedStats.averageTalkTime, true) }}</span></div>
							<div v-if="key=='TOTALTALKTIME'" class="stats-value">{{ shortduration(aggregatedStats.totalTalkTime, false) }}<span class="stats-value-label">{{ shortduration(aggregatedStats.totalTalkTime, true) }}</span></div>
							<div v-if="key=='LONGESTWAITTIME'" class="stats-value">{{ shortduration(aggregatedStats.longestWaitTime, false) }}<span class="stats-value-label">{{ shortduration(aggregatedStats.longestWaitTime, true) }}</span></div>
							<div v-if="key=='LASTWAITTIME'" class="stats-value">{{ shortduration(aggregatedStats.lastWaitTime, false) }}<span class="stats-value-label">{{ shortduration(aggregatedStats.lastWaitTime, true) }}</span></div>
							<div v-if="key=='SERVICELEVEL'" class="stats-value">{{ aggregatedStats.serviceLevel }}<span class="stats-value-label">%</span></div>
							<div v-if="key=='SERVICELEVELCRITICAL'" class="stats-value">{{ aggregatedStats.serviceLevelCritical }}<span class="stats-value-label">%</span></div>
						</div>
					</div>
				</div>

			</div>
		</div>
	</PanelDraggable>
</template>

<script setup lang="ts">
import { useSettingsStore } from '@/stores/Settings'
import { useMediaDevicesStore } from '@/stores/MediaDevices'
import { useDashboardsStore } from '@/stores/Dashboards'
import { useEmployeesStore } from '@/stores/Employees'
import { useQueuesStore } from '@/stores/Queues'
import { useApiStore } from '@/stores/Api'
import { useI18n } from 'vue-i18n'
import globalEvents from '@/utilities/GlobalEvents'
import GlyphIcon from '@/components/GlyphIcon.vue'
import PanelDraggable from '@/components/dashboard/PanelDraggable.vue'
import MultiQueueCall from '@/components/dashboard/MultiQueueCall.vue'
import MultiQueueMember from '@/components/dashboard/MultiQueueMember.vue'
import CallDraggable from '@/components/dashboard/CallDraggable.vue'
import { PbxAppDashboardItemQueueStats, type PbxAppDashboardItemMultiQueue, type PbxAppQueuesAllItem, type PbxAppQueuesCaller, type PbxAppQueuesMember } from '@/TcxApi/PbxApp'
import { computed, onUnmounted, ref, watch } from 'vue'
import { type PbxQueueStatsResponse } from '@/TcxApi/PbxQueue'
import { dynamicSort } from '@/utilities/Tools'
import { GlyphIconName } from '@/stores/GlyphIcons.types'
import type { MenuItem } from 'primevue/menuitem'
import Menu from 'primevue/menu'
import { CallDragItemType, CallDragTargetType } from '@/utilities/CallDrag'
import { shortduration } from '@/utilities/filters'

const ss = useSettingsStore()
const mediaDevices = useMediaDevicesStore()
const dashboards = useDashboardsStore()
const employees = useEmployeesStore()
const queues = useQueuesStore()
const api = useApiStore()
const { t } = useI18n()

interface Props {
	data: PbxAppDashboardItemMultiQueue
}

enum CommonState {
	GREEN = 'GREEN',
	YELLOW = 'YELLOW',
	RED = 'RED'
}

interface EnhancedCaller extends PbxAppQueuesCaller {
	queueId: string
	queueName: string
	queuePriority: number
	serviceLevel: number
	serviceLevelCritical: number
}

interface UniqueMember extends PbxAppQueuesMember {
	queueIds: string[]
	queueNames: string[]
}

const props = defineProps<Props>()
const settings = dashboards.dashboard?.items.find(item => item === props.data) as PbxAppDashboardItemMultiQueue
const isDraggable = ref(false)
const availableQueues = ref<{ value: string, label: string, name: string }[]>([])
const commonState = ref<CommonState>(CommonState.GREEN)
const initRunning = ref(false)
const collapsed = ref<boolean>(ss.getGeneric(`${ss.settings.dashboardId}-${props.data.type}-${props.data.column}-${props.data.position}-collapsed`) || false)
const collapsedCallers = ref<boolean>(ss.getGeneric(`${ss.settings.dashboardId}-${props.data.type}-${props.data.column}-${props.data.position}-collapsedCallers`) || false)
const collapsedMembers = ref<boolean>(ss.getGeneric(`${ss.settings.dashboardId}-${props.data.type}-${props.data.column}-${props.data.position}-collapsedMembers`) || false)
const collapsedStatistics = ref<boolean>(ss.getGeneric(`${ss.settings.dashboardId}-${props.data.type}-${props.data.column}-${props.data.position}-collapsedStatistics`) || false)
const statsPicker = ref<PbxAppDashboardItemQueueStats | null>(null)
const membershipMenu = ref<InstanceType<typeof Menu>>()
const aggregatedStats = ref<Omit<PbxQueueStatsResponse, '_id' | 'name' | 'date'>>({ noMembers: 0, allBusy: 0, timeout: 0, cancelled: 0, answered: 0, unanswered: 0, total: 0, averageWaitTime: 0, averageTalkTime: 0, totalTalkTime: 0, longestWaitTime: 0, lastWaitTime: 0, serviceLevel: 0, serviceLevelCritical: 0 })

// Resolved from the store on every read so replaced/reloaded queue objects are never referenced stale
const queueList = computed(() => {
	return props.data._ids
		.map(queueId => queues.getQueue(queueId))
		.filter((queue): queue is PbxAppQueuesAllItem => queue !== undefined)
})

const headerTitle = computed(() => {
	if (queueList.value.length === 0) {
		return settings.title + ' - ' + t('multiQueue.noQueuesSelected')
	} else if (queueList.value.length === 1) {
		return queueList.value[0]?.name || settings.title
	} else {
		return settings.title + ' (' + t('multiQueue.queuesCount', { count: queueList.value.length }) + ')'
	}
})

const totalCalls = computed(() => {
	return queueList.value.reduce((sum, queue) => sum + queue.callers.length, 0)
})

const sortedCalls = computed(() => {
	const calls: EnhancedCaller[] = []

	for (const queue of queueList.value) {
		for (const caller of queue.callers) {
			calls.push({
				...caller,
				queueId: queue._id,
				queueName: queue.name,
				queuePriority: queue.priority || 1,
				serviceLevel: queue.serviceLevel,
				serviceLevelCritical: queue.serviceLevelCritical
			})
		}
	}

	// Sort by queue priority (ascending), then by entered time (ascending)
	calls.sort((a, b) => {
		if (a.queuePriority !== b.queuePriority) {
			return a.queuePriority - b.queuePriority
		}
		return a.entered.getTime() - b.entered.getTime()
	})

	return calls
})

const uniqueMembers = computed(() => {
	const memberMap = new Map<string, UniqueMember>()

	for (const queue of queueList.value) {
		for (const member of queue.members) {
			const key = member.extension || member.number || member._id

			if (memberMap.has(key)) {
				const existing = memberMap.get(key)! // eslint-disable-line @typescript-eslint/no-non-null-assertion

				existing.queueIds.push(queue._id)
				existing.queueNames.push(queue.name)
				// Aggregate stats
				existing.calls += member.calls
				existing.seconds += member.seconds
			} else {
				memberMap.set(key, {
					...member,
					queueIds: [queue._id],
					queueNames: [queue.name]
				})
			}
		}
	}

	return Array.from(memberMap.values()).sort(dynamicSort('priority'))
})

const dynamicQueues = computed(() => {
	return queueList.value
		.filter(queue => queue.dynamic)
		.map(queue => ({ value: queue._id, label: queue.name }))
})

const combinedQueues = computed(() => {
	return {
		_id: 'multi',
		name: 'Multi Queue',
		dynamic: false,
		member: false,
		serviceLevel: 0,
		serviceLevelCritical: 0,
		priority: 1,
		callers: sortedCalls.value,
		members: uniqueMembers.value
	}
})

const statsHeight = computed(() => {
	if (props.data.showStats) {
		const rows = Math.ceil(props.data.stats.length / 4)

		return (rows * 56).toString() + 'px'
	} else {
		return '0px'
	}
})

const statsAvailable = computed(() => {
	const available: { value: PbxAppDashboardItemQueueStats, label: string }[] = []

	for (const key in PbxAppDashboardItemQueueStats) {
		if (!settings.stats.includes(key as PbxAppDashboardItemQueueStats)) {
			available.push({ value: key as PbxAppDashboardItemQueueStats, label: t('queue.stats2.' + key) })
		}
	}

	return available
})

const membershipMenuItems = computed<MenuItem[]>(() => {
	return queueList.value
		.filter(queue => queue.dynamic)
		.map(queue => {
			const isMember = queue.members.some(member => member.extension === employees.me?.primaryExtension?._id)

			return {
				label: queue.name,
				icon: isMember ? 'pi pi-check' : 'no-icon',
				command: () => toggleQueueMembership(queue._id)
			}
		})
})

// #region EDIT functions

function addStat() {
	if (statsPicker.value) {
		settings.stats.push(statsPicker.value)
		statsPicker.value = null
	}
}

function removeStat(item: PbxAppDashboardItemQueueStats) {
	const index = settings.stats.indexOf(item)
	if (index > -1) {
		settings.stats.splice(index, 1)
	}
}

function remove() {
	dashboards.removeItem(props.data.column, props.data.position)
}

function mouseOverHeader(itIs: boolean) {
	if (itIs && dashboards.editMode && !dashboards.dragItem) {
		isDraggable.value = true
	} else {
		isDraggable.value = false
	}
}

function inputValidator(event: KeyboardEvent, rx: RegExp) {
	if (event.key.length === 1 && !rx.test(event.key)) {
		event.preventDefault()
	}
}

function inputPasteValidator(event: ClipboardEvent, rx: RegExp) {
	const clipboardData = event.clipboardData?.getData('text/plain')
	if (clipboardData && !rx.test(clipboardData)) {
		event.preventDefault()
	}
}

// #endregion

// #region VIEW functions

function toggleCollapse() {
	collapsed.value = !collapsed.value
	ss.setGeneric(`${ss.settings.dashboardId}-${props.data.type}-${props.data.column}-${props.data.position}-collapsed`, collapsed.value)
}

function toggleCollapseCallers() {
	collapsedCallers.value = !collapsedCallers.value
	ss.setGeneric(`${ss.settings.dashboardId}-${props.data.type}-${props.data.column}-${props.data.position}-collapsedCallers`, collapsedCallers.value)
}

function toggleCollapseMembers() {
	collapsedMembers.value = !collapsedMembers.value
	ss.setGeneric(`${ss.settings.dashboardId}-${props.data.type}-${props.data.column}-${props.data.position}-collapsedMembers`, collapsedMembers.value)
}

function toggleCollapseStatistics() {
	collapsedStatistics.value = !collapsedStatistics.value
	ss.setGeneric(`${ss.settings.dashboardId}-${props.data.type}-${props.data.column}-${props.data.position}-collapsedStatistics`, collapsedStatistics.value)
}

async function eventQueueDeleted(deletedQueue: PbxAppQueuesAllItem) {
	if (settings._ids.includes(deletedQueue._id)) {
		settings._ids = settings._ids.filter(id => id !== deletedQueue._id)

		if (settings._ids.length === 0) {
			dashboards.removeItem(props.data.column, props.data.position)

			try {
				await dashboards.save()
				globalEvents.emit('CommonErrorModal', { header: t('queue.panelDeletedHeader'), message: t('queue.panelDeletedMessage').replace('%%name%%', deletedQueue.name) })
			}
			catch (_) { /** Ignore */ }
		}
	}
}

function toggleMembershipMenu(event: MouseEvent) {
	membershipMenu.value?.toggle(event)
}

async function toggleQueueMembership(queueId: string) {
	const queue = queueList.value.find(qu => qu._id === queueId)
	if (!queue) { return }

	const isMember = queue.members.some(member => member.extension === employees.me?.primaryExtension?._id)

	if (isMember) {
		// Leave queue
		try {
			await api.del(`/pbx/queue/${queue._id}/member/${employees.me?.primaryExtension?._id}`)
		}
		catch (_) { /** Ignore */ }
	} else {
		// Join queue
		try {
			await api.post(`/pbx/queue/${queue._id}/member`, { extension: employees.me?.primaryExtension?._id })
		}
		catch (_) { /** Ignore */ }
	}
}

async function loadStats() {
	if (props.data.showStats && props.data._ids.length > 0) {
		const statsPromises = props.data._ids.map(queueId =>
			api.get<PbxQueueStatsResponse>(`/pbx/queue/${queueId}/stats`).catch(() => null)
		)

		const results = await Promise.all(statsPromises)

		// Aggregate stats
		const stats = {
			noMembers: 0,
			allBusy: 0,
			timeout: 0,
			cancelled: 0,
			answered: 0,
			unanswered: 0,
			total: 0,
			averageWaitTime: 0,
			averageTalkTime: 0,
			totalTalkTime: 0,
			longestWaitTime: 0,
			lastWaitTime: 0,
			serviceLevel: 0,
			serviceLevelCritical: 0
		}

		let validCount = 0

		for (const result of results) {
			if (result) {
				validCount++
				stats.noMembers += result.data.noMembers
				stats.allBusy += result.data.allBusy
				stats.timeout += result.data.timeout
				stats.cancelled += result.data.cancelled
				stats.answered += result.data.answered
				stats.unanswered += result.data.unanswered
				stats.total += result.data.total
				stats.averageWaitTime += result.data.averageWaitTime
				stats.averageTalkTime += result.data.averageTalkTime
				stats.totalTalkTime += result.data.totalTalkTime
				stats.longestWaitTime = Math.max(stats.longestWaitTime, result.data.longestWaitTime)
				stats.lastWaitTime = result.data.lastWaitTime // Keep the most recent
				stats.serviceLevel += result.data.serviceLevel
				stats.serviceLevelCritical += result.data.serviceLevelCritical
			}
		}

		// Average the time-based and percentage stats
		if (validCount > 0) {
			stats.averageWaitTime = Math.round(stats.averageWaitTime / validCount)
			stats.averageTalkTime = Math.round(stats.averageTalkTime / validCount)
			stats.serviceLevel = Math.round(stats.serviceLevel / validCount)
			stats.serviceLevelCritical = Math.round(stats.serviceLevelCritical / validCount)
		}

		aggregatedStats.value = stats
	}
}

function updateCommonState() {
	let color: CommonState = CommonState.GREEN

	for (const call of sortedCalls.value) {
		const seconds = Math.round((new Date().getTime() - call.entered.getTime()) / 1000)

		if (seconds > call.serviceLevel && seconds <= call.serviceLevelCritical) {
			color = CommonState.YELLOW
		} else if (seconds > call.serviceLevelCritical) {
			color = CommonState.RED
		}
	}

	if (commonState.value !== color) {
		commonState.value = color
		if (color !== CommonState.GREEN && ss.settings.queueCriticalSound) {
			playCommonStateSound()
		}
	}
}

async function playCommonStateSound() {
	const parent = document.getElementById('SipPhoneVideoContainer')
	const ae = document.createElement('audio')

	if (parent) {
		ae.id = 'RingTonePlayer'
		ae.autoplay = true
		ae.loop = false
		parent.appendChild(ae)
		if (ss.settings.media.preferredRingerOutputs[0]) {
			await ae.setSinkId(ss.settings.media.preferredRingerOutputs[0])
		}
		ae.src = 'https://audio.telecomx.dk/619e5328aa69b00a66f57f6a.mp3' // Critical sound - backup via MongoDB:CommonFiles
		ae.currentTime = 0
		ae.volume = mediaDevices.ringerVolume
		ae.play()
		setTimeout(() => {
			parent.removeChild(ae)
			ae.src = ''
		}, 7000)
	}
}

// #endregion

// Initialize

async function init() {
	await employees.ready()

	if (initRunning.value) { return }
	initRunning.value = true

	loadStats()

	if (props.data.showStats) {
		if (!props.data.stats || props.data.stats.length === 0) {
			settings.stats = [ PbxAppDashboardItemQueueStats.UNANSWERED, PbxAppDashboardItemQueueStats.ANSWERED, PbxAppDashboardItemQueueStats.AVERAGEWAITTIME, PbxAppDashboardItemQueueStats.LASTWAITTIME]
		}
	}

	initRunning.value = false
}

init()

if (dashboards.editMode) {
	availableQueues.value.push(...queues.list)
}

globalEvents.on('Queues:Deleted', eventQueueDeleted)
globalEvents.on('Queues:Loaded', init)
globalEvents.on('OneSecond', updateCommonState)
globalEvents.on('OneMinute', loadStats)

onUnmounted(() => {
	initRunning.value = true // ensures init is not called after unmount
	globalEvents.off('Queues:Deleted', eventQueueDeleted)
	globalEvents.off('Queues:Loaded', init)
	globalEvents.off('OneSecond', updateCommonState)
	globalEvents.off('OneMinute', loadStats)
})

// When edit mode is enabled, load the list of available queues
watch(() => dashboards.editMode, (newValue) => {
	if (newValue) {
		availableQueues.value = []
		availableQueues.value.push(...queues.list)
		availableQueues.value.sort(dynamicSort('name'))
	} else {
		availableQueues.value = []
		init()
	}
})

// Watch for changes in queue selection (the queue list itself is a computed and updates automatically)
watch(() => props.data._ids, () => {
	if (!dashboards.editMode) {
		loadStats()
	}
}, { deep: true })
</script>

<style scoped>
	.section-header {
		border-radius: 5px;
		background-color: var(--bg-color-dashboard-header);
		color: var(--txt-color-dashboard-header);
		padding-left: 10px;
		padding-right: 2px;
		margin: 4px;
		font-size: 12px;
		line-height: 13px;
		font-weight: 300;
		display: flex;
		justify-content: space-between;
		align-items: center;
	}
	.settings-box {
		margin: 5px 5px;
	}
	.view-box {
		/* margin: 5px 5px; */
		overflow: auto;
		transition: 500ms;
	}
	.list-item {
		display: inline-block;
		transition: all 0.7s;
		width: 100%;
	}

	/* ENTER */
	.list2-enter-from {
	opacity: 0;
	transform: translateY(15px);
	}
	.list2-enter-active {
	transition: opacity 0.3s ease, transform 0.3s ease;
	}
	.list2-enter-to {
	opacity: 1;
	transform: translateY(0);
	}

	/* LEAVE */
	.list2-leave-from {
	opacity: 1;
	transform: translateY(0);
	}
	.list2-leave-active {
	transition: opacity 0.3s ease, transform 0.3s ease;
	}
	.list2-leave-to {
	opacity: 0;
	transform: translateY(15px);
	}

	.vertical-scroll {
		overflow-y: scroll;
		overflow-x: hidden;
		margin-bottom: 5px;
	}
	.vertical-scroll::-webkit-scrollbar {
		width: 1px;
		background: transparent;
	}
	.vertical-scroll::-webkit-scrollbar-thumb {
		background: var(--pn-primary-1);
	}
	.stats-box {
		display: grid;
		grid-template-columns: 1fr 1fr 1fr 1fr;
		column-gap: 6px;
		margin-left: 4px;
		margin-right: 4px;
		overflow: hidden;
	}
	.stats-box > div {
		border-radius: 10px;
		background-image: radial-gradient(circle at left top, var(--p-surface-50), var(--p-surface-200));
		box-shadow: 0px 0px 3px 0px #66666640;
		padding-top: 5px;
		padding-bottom: 4px;
		margin-bottom: 5px;
		border: 1px solid var(--p-form-field-border-color);
	}
	.stats-head {
		font-size: 10px;
		text-align: center;
		line-height: 10px;
		color: var(--p-text-color);
		padding-bottom: 2px;
	}
	.stats-value {
		font-size: 16px;
		line-height: 16px;
		color: #BBAC91;
		font-weight: 600;
		text-align: center;
	}
	.stats-value-label {
		font-size: 11px;
		color: var(--p-text-color);
		font-weight: 300;
		margin-left:2px;
	}
	@media(prefers-color-scheme: dark) {
		.stats-box > div {
			background-image: radial-gradient(circle at left top, var(--p-surface-800), var(--p-surface-950));
			box-shadow: 0px 0px 3px 0px #ffffff40;
			border: 1px solid #333333;
		}
		.stats-head {
			color: var(--p-text-muted-color);
		}
		.stats-value-label {
			color: var(--p-text-muted-color);
		}
	}
	.variable-info {
		margin-top: -7px;
		line-height: 12px;
		font-size: 11px;
		margin-bottom:10px;
		color: var(--p-text-muted-color);
	}
	.section-label {
		font-size: 12px;
		margin-left: -7px;
	}
	.stats-edit-box {
		border: 1px solid var(--p-form-field-border-color);
		border-radius: 5px;
		background-color: var(--p-form-field-background);
		padding: 2px;
	}
	.stats-edit-item {
		width: 25%;
		cursor: s-resize;
		flex: 1 0 22%;
		font-size: 10px;
		text-align: center;
		line-height: 11px;
		padding: 4px 1px;
		background-color: var(--p-surface-200);
		border: 1px solid var(--p-form-field-border-color);
		border-radius: 5px 5px;
	}
	@media(prefers-color-scheme: dark) {
		.stats-edit-item {
			background-color: var(--p-surface-950);
		}
	}
</style>
